import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { AppIndexScene } from 'shared-ui/lib/customs/react/app-index-scene/AppIndexScene';
import { serviceCallApplicationColumn } from '../../columns/service-call-application.column';
import {
  useApproveServiceCallApplicationMutation,
  useReactivateServiceCallApplicationMutation,
  useRejectServiceCallApplicationMutation,
  useRevokeServiceCallApplicationMutation,
  useServiceCallApplicationsLazyQuery,
} from '../../generated/graphql';
import { useGQLContext } from '../../hooks/use-gql-context';

export const ServiceCallApplicationIndexScene = (): JSX.Element => {
  const { t } = useTranslation('ServiceCallApplicationIndexScene');
  const context = useGQLContext();

  const pendingQuery = useServiceCallApplicationsLazyQuery({ context });
  const approvedQuery = useServiceCallApplicationsLazyQuery({ context });
  const inactiveQuery = useServiceCallApplicationsLazyQuery({ context });
  const rejectedQuery = useServiceCallApplicationsLazyQuery({ context });

  const refetchAll = () => {
    pendingQuery[1].refetch?.();
    approvedQuery[1].refetch?.();
    inactiveQuery[1].refetch?.();
    rejectedQuery[1].refetch?.();
  };

  const [approve] = useApproveServiceCallApplicationMutation({
    context,
    onCompleted: (data) => {
      if (data.aApproveServiceCallApplication) {
        toast.success(t('approved'));
        refetchAll();
      }
    },
  });

  const [reject] = useRejectServiceCallApplicationMutation({
    context,
    onCompleted: (data) => {
      if (data.aRejectServiceCallApplication) {
        toast.success(t('rejected'));
        refetchAll();
      }
    },
  });

  const [revoke] = useRevokeServiceCallApplicationMutation({
    context,
    onCompleted: (data) => {
      if (data.aRevokeServiceCallApplication) {
        toast.success(t('accessRevoked'));
        refetchAll();
      }
    },
  });

  const [reactivate] = useReactivateServiceCallApplicationMutation({
    context,
    onCompleted: (data) => {
      if (data.aReactivateServiceCallApplication) {
        toast.success(t('reactivated'));
        refetchAll();
      }
    },
  });

  const actionsColumn = serviceCallApplicationColumn.actions({
    labels: {
      approve: t('approve'),
      reject: t('reject'),
      revokeAccess: t('revokeAccess'),
      reactivate: t('reactivate'),
    },
    onApprove: (row) => {
      if (confirm(t('confirmApprove', { name: row.officialName }))) {
        approve({ variables: { id: row.id } });
      }
    },
    onReject: (row) => {
      if (confirm(t('confirmReject', { name: row.officialName }))) {
        reject({ variables: { id: row.id } });
      }
    },
    onRevokeAccess: (row) => {
      if (confirm(t('confirmRevokeAccess', { name: row.officialName }))) {
        revoke({ variables: { id: row.id } });
      }
    },
    onReactivate: (row) => {
      if (confirm(t('confirmReactivate', { name: row.officialName }))) {
        reactivate({ variables: { id: row.id } });
      }
    },
  });

  const baseColumns = [
    serviceCallApplicationColumn.officialName,
    serviceCallApplicationColumn.businessId,
    serviceCallApplicationColumn.contactName,
    serviceCallApplicationColumn.email,
    serviceCallApplicationColumn.status,
    serviceCallApplicationColumn.createdAt,
  ];

  const handledColumns = [...baseColumns, serviceCallApplicationColumn.handledAt];

  return (
    <div className="space-y-8">
      <AppIndexScene
        tNs="ServiceCallApplicationIndexScene"
        title={t('pendingTitle')}
        lazyQuery={pendingQuery}
        where={{ status: 'pending' }}
        columns={[...baseColumns, actionsColumn, serviceCallApplicationColumn.link]}
        dataPath="aServiceCallApplications.list"
        totalCountPath="aServiceCallApplications.totalCount"
      />

      <AppIndexScene
        tNs="ServiceCallApplicationIndexScene"
        title={t('approvedTitle')}
        lazyQuery={approvedQuery}
        where={{ status: 'approved' }}
        columns={[...handledColumns, actionsColumn, serviceCallApplicationColumn.link]}
        dataPath="aServiceCallApplications.list"
        totalCountPath="aServiceCallApplications.totalCount"
      />

      <AppIndexScene
        tNs="ServiceCallApplicationIndexScene"
        title={t('inactiveTitle')}
        lazyQuery={inactiveQuery}
        where={{ status: 'inactive' }}
        columns={[...handledColumns, actionsColumn, serviceCallApplicationColumn.link]}
        dataPath="aServiceCallApplications.list"
        totalCountPath="aServiceCallApplications.totalCount"
      />

      <AppIndexScene
        tNs="ServiceCallApplicationIndexScene"
        title={t('rejectedTitle')}
        lazyQuery={rejectedQuery}
        where={{ status: 'rejected' }}
        columns={[...handledColumns, serviceCallApplicationColumn.link]}
        dataPath="aServiceCallApplications.list"
        totalCountPath="aServiceCallApplications.totalCount"
      />
    </div>
  );
};
